/**
 * Fehlermeldungen rund um Portal-Konten in deutschen Klartext übersetzen.
 *
 * Firebase liefert sowohl bei Auth-Aufrufen als auch bei Callable Functions
 * englische Rohtexte („Firebase: Error (auth/email-already-in-use)."). Die sind
 * für den Disponenten am Portal unbrauchbar — und wurden bisher unverändert
 * durchgereicht.
 *
 * Callable Functions setzen ihre eigene, bereits deutsche Meldung; die wird
 * bevorzugt, sofern sie nicht nur ein generischer Rahmentext ist.
 */

const AUTH_MESSAGES = {
  'auth/email-already-in-use': 'Diese E-Mail-Adresse wird bereits für einen anderen Zugang verwendet.',
  'auth/email-already-exists': 'Diese E-Mail-Adresse wird bereits für einen anderen Zugang verwendet.',
  'auth/invalid-email': 'Die E-Mail-Adresse hat kein gültiges Format.',
  'auth/weak-password': 'Das Passwort ist zu schwach — bitte mindestens 8 Zeichen verwenden.',
  'auth/invalid-password': 'Das Passwort erfüllt die Anforderungen nicht (mindestens 8 Zeichen).',
  'auth/user-not-found': 'Für dieses Konto existiert kein Login-Account.',
  'auth/wrong-password': 'Das aktuelle Passwort ist nicht korrekt.',
  'auth/invalid-credential': 'Das aktuelle Passwort ist nicht korrekt.',
  'auth/requires-recent-login': 'Aus Sicherheitsgründen bitte neu anmelden und den Vorgang wiederholen.',
  'auth/network-request-failed': 'Keine Verbindung zum Server — bitte Internetverbindung prüfen.',
  'auth/too-many-requests': 'Zu viele Versuche. Bitte einige Minuten warten und erneut versuchen.',
  'auth/operation-not-allowed': 'Die Anmeldung per E-Mail und Passwort ist in diesem Projekt nicht aktiviert.',
};

const FUNCTION_MESSAGES = {
  'functions/permission-denied': 'Ihr Konto hat für diese Aktion keine Berechtigung.',
  'functions/unauthenticated': 'Ihre Sitzung ist abgelaufen — bitte neu anmelden.',
  'functions/not-found':
    'Die benötigte Server-Funktion ist in diesem Projekt nicht verfügbar. ' +
    'Bitte die Cloud Functions deployen.',
  'functions/internal': 'Auf dem Server ist ein Fehler aufgetreten. Bitte später erneut versuchen.',
  'functions/unavailable': 'Der Server ist derzeit nicht erreichbar. Bitte später erneut versuchen.',
  'functions/deadline-exceeded': 'Die Anfrage hat zu lange gedauert. Bitte erneut versuchen.',
};

// Rahmentexte, die das SDK selbst erzeugt — sie sagen dem Nutzer nichts und
// werden durch die Code-Zuordnung ersetzt.
const GENERIC_FUNCTION_MESSAGES = new Set([
  'internal',
  'INTERNAL',
  'unknown',
  'UNKNOWN',
  'deadline-exceeded',
  'unavailable',
  'not-found',
  'NOT_FOUND',
]);

/**
 * @param {unknown} error Fehlerobjekt aus Firebase Auth, httpsCallable oder ein
 *   bereits ausgepackter String.
 * @param {string} [fallback] Text, wenn sich nichts Besseres bestimmen lässt.
 * @returns {string}
 */
export const accountErrorMessage = (error, fallback = 'Die Aktion konnte nicht ausgeführt werden.') => {
  if (!error) return fallback;
  if (typeof error === 'string') {
    // Bereits übersetzte Slice-Meldungen unverändert lassen, rohe
    // Firebase-Strings dagegen auf ihren Code zurückführen.
    const codeMatch = error.match(/\((auth\/[a-z-]+|functions\/[a-z-]+)\)/);
    if (codeMatch) {
      return AUTH_MESSAGES[codeMatch[1]] || FUNCTION_MESSAGES[codeMatch[1]] || fallback;
    }
    return error.trim() || fallback;
  }

  const code = String(error.code || '');
  if (AUTH_MESSAGES[code]) return AUTH_MESSAGES[code];

  // Callable Functions: die serverseitige Meldung ist bereits deutsch und
  // konkreter als jede Zuordnung hier — sofern sie nicht nur der Code ist.
  const message = String(error.message || '').trim();
  if (code.startsWith('functions/')) {
    if (message && !GENERIC_FUNCTION_MESSAGES.has(message)) return message;
    return FUNCTION_MESSAGES[code] || fallback;
  }

  if (message.startsWith('Firebase:')) {
    const codeMatch = message.match(/\((auth\/[a-z-]+)\)/);
    if (codeMatch && AUTH_MESSAGES[codeMatch[1]]) return AUTH_MESSAGES[codeMatch[1]];
    return fallback;
  }

  return message || fallback;
};

export default accountErrorMessage;
